/**
 * OccupationalExam Edit Page
 * Page for editing existing occupational exam
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { occupationalExamService } from '../../services/occupationalExamService';
import { OccupationalExam, OccupationalExamFormData } from '../../types/occupationalExam';
import { OccupationalExamForm } from './components/OccupationalExamForm';

export const OccupationalExamEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [exam, setExam] = useState<OccupationalExam | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    const loadExam = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await occupationalExamService.getOccupationalExamById(id);
        setExam(data);
      } catch (err: unknown) {
        setFalha(
          falhaDeCarga(err, 'Não foi possível carregar o exame ocupacional. Tente de novo.'),
        );
      } finally {
        setLoading(false);
      }
    };

    loadExam();
  }, [id]);

  const handleSubmit = async (data: OccupationalExamFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await occupationalExamService.updateOccupationalExam(id, data);
      navigate('/occupational-exams');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao atualizar exame ocupacional');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/occupational-exams');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/home' },
    { label: 'Medicina', path: '/medicina' },
    { label: 'Exames Ocupacionais', path: '/occupational-exams' },
    { label: 'Editar Exame' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Exame Ocupacional">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!exam) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Exame Ocupacional">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Exame ocupacional não encontrado.')}
          rotuloDoBotao="Voltar para exames ocupacionais"
          aoVoltar={() => navigate('/occupational-exams')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Exame Ocupacional"
      subtitle={exam.examName}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <OccupationalExamForm
            initialData={exam}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={isSubmitting}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default OccupationalExamEditPage;
